/**
 * Git-style conflict model over a two-version diff.
 *
 * The suggestions/compare view diffs a "from" (ours) and "to" (theirs) body.
 * This turns that into an ordered list of segments — unchanged text and
 * conflict hunks — that can be rendered with <<<<<<< / ======= / >>>>>>>
 * markers and resolved per-hunk into a single merged markdown body.
 *
 * Conflicts are built from an *uncollapsed* line diff so the merged output
 * reconstructs the whole document (buildTrackChanges collapses far context
 * into skip markers, which would drop text on merge).
 */
import { diffLines } from "./diff";
import {
  diffBlocksToTrackChanges,
  type TrackChangeBlock,
  type TrackSpan,
} from "./track-changes";

export type Resolution = "ours" | "theirs" | "both";

export interface TextSegment {
  type: "text";
  text: string;
}

export interface ConflictSegment {
  type: "conflict";
  id: string;
  ours: string;
  theirs: string;
}

export type MergeSegment = TextSegment | ConflictSegment;

function spansToOurs(spans: TrackSpan[]): string {
  return spans
    .filter((s) => s.type === "del" || s.type === "same")
    .map((s) => s.text)
    .join("");
}

function spansToTheirs(spans: TrackSpan[]): string {
  return spans
    .filter((s) => s.type === "ins" || s.type === "same")
    .map((s) => s.text)
    .join("");
}

/** Convert track-change blocks (built without context collapsing) into segments. */
export function buildConflicts(blocks: TrackChangeBlock[]): MergeSegment[] {
  const segments: MergeSegment[] = [];
  for (const block of blocks) {
    if (block.type === "unchanged") {
      segments.push({ type: "text", text: block.markdown });
    } else if (block.type === "revision") {
      segments.push({
        type: "conflict",
        id: block.id,
        ours: spansToOurs(block.spans),
        theirs: spansToTheirs(block.spans),
      });
    }
    // 'skip' blocks cannot occur here — we feed an uncollapsed diff.
  }
  return segments;
}

/** Build conflict segments directly from the two document bodies. */
export function buildConflictsFromText(
  oldText: string,
  newText: string,
): MergeSegment[] {
  const blocks = diffBlocksToTrackChanges(
    [{ type: "lines", lines: diffLines(oldText, newText) }],
    "c",
  );
  return buildConflicts(blocks);
}

export function conflictCount(segments: MergeSegment[]): number {
  return segments.reduce((n, s) => (s.type === "conflict" ? n + 1 : n), 0);
}

function resolveHunk(seg: ConflictSegment, res: Resolution): string {
  if (res === "ours") return seg.ours;
  if (res === "theirs") return seg.theirs;
  const ours = seg.ours.replace(/\s+$/, "");
  const theirs = seg.theirs.replace(/^\s+/, "");
  return ours && theirs ? `${ours}\n${theirs}` : ours || theirs;
}

/**
 * Merge into a single markdown body. `resolutions` maps a conflict id to a
 * choice; any hunk without an entry uses `fallback` (default 'theirs', i.e.
 * the proposed version). All-'theirs' reproduces the new body; all-'ours'
 * reproduces the old body.
 */
export function mergeResolved(
  segments: MergeSegment[],
  resolutions: Record<string, Resolution>,
  fallback: Resolution = "theirs",
): string {
  return segments
    .map((seg) =>
      seg.type === "text"
        ? seg.text
        : resolveHunk(seg, resolutions[seg.id] ?? fallback),
    )
    .join("\n");
}

/**
 * Serialize with literal conflict markers for every hunk (for a raw/copyable
 * view). `resolutions` lets already-resolved hunks render as plain text.
 */
export function serializeWithMarkers(
  segments: MergeSegment[],
  oursLabel: string,
  theirsLabel: string,
  resolutions: Record<string, Resolution> = {},
): string {
  return segments
    .map((seg) => {
      if (seg.type === "text") return seg.text;
      const res = resolutions[seg.id];
      if (res) return resolveHunk(seg, res);
      return [
        `<<<<<<< ${oursLabel}`,
        seg.ours,
        "=======",
        seg.theirs,
        `>>>>>>> ${theirsLabel}`,
      ].join("\n");
    })
    .join("\n");
}
